import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const PRESETS = [
  { label: '1+0', tc: 60, inc: 0 },
  { label: '3+0', tc: 180, inc: 0 },
  { label: '3+2', tc: 180, inc: 2 },
  { label: '5+0', tc: 300, inc: 0 },
  { label: '5+3', tc: 300, inc: 3 },
  { label: '10+0', tc: 600, inc: 0 },
  { label: '10+5', tc: 600, inc: 5 },
  { label: '15+10', tc: 900, inc: 10 },
];

/** Same formula as backend ChessSpeedClass.fromTimeControl */
export function speedClassFromTimeControl(tcSeconds, incSeconds, daysPerMove = null) {
  if (daysPerMove != null && Number(daysPerMove) > 0) return 'CORRESPONDENCE';
  const tc = tcSeconds != null && tcSeconds > 0 ? tcSeconds : 600;
  const inc = incSeconds != null && incSeconds > 0 ? incSeconds : 0;
  const estimated = tc + 40 * inc;
  if (estimated < 179) return 'BULLET';
  if (estimated < 479) return 'BLITZ';
  if (estimated < 1499) return 'RAPID';
  return 'CLASSICAL';
}

/** Club defaults when deep-linking a challenge by speed class. */
export function defaultTimeControlForSpeed(speed) {
  switch (String(speed || '').toUpperCase()) {
    case 'BULLET':
      return { timeControlSeconds: 60, incrementSeconds: 0, daysPerMove: 0 };
    case 'BLITZ':
      return { timeControlSeconds: 300, incrementSeconds: 0, daysPerMove: 0 };
    case 'RAPID':
      return { timeControlSeconds: 600, incrementSeconds: 0, daysPerMove: 0 };
    case 'CLASSICAL':
      return { timeControlSeconds: 900, incrementSeconds: 10, daysPerMove: 0 };
    case 'CORRESPONDENCE':
      return { timeControlSeconds: 0, incrementSeconds: 0, daysPerMove: 3 };
    default:
      return { timeControlSeconds: 600, incrementSeconds: 0, daysPerMove: 0 };
  }
}

function speedLabel(speed, t) {
  return t(`playing.speed.${speed}`, {
    defaultValue: speed.charAt(0) + speed.slice(1).toLowerCase(),
  });
}

export default function TimeControlFields({
  timeControlSeconds,
  incrementSeconds,
  onChange,
  /** Hub layout: calm pairing-style grid instead of pill wrap */
  layout = 'default',
}) {
  const { t } = useTranslation();
  const selected = PRESETS.find((p) => p.tc === timeControlSeconds && p.inc === incrementSeconds);
  const [customOpen, setCustomOpen] = useState(!selected);

  const showCustom = customOpen || !selected;
  const activeSpeed = speedClassFromTimeControl(timeControlSeconds, incrementSeconds);
  const hub = layout === 'hub';

  return (
    <div className={hub ? 'playing-hub-tc space-y-2.5' : 'space-y-3'}>
      <p className={hub ? 'playing-hub-field-label' : 'text-sm font-medium text-slate-700'}>
        {t('playing.timeControl.label')}
      </p>
      <div className={hub ? 'playing-hub-tc-grid' : 'flex flex-wrap gap-2'}>
        {PRESETS.map((p) => {
          const active = !customOpen && selected && selected.tc === p.tc && selected.inc === p.inc;
          const speed = speedClassFromTimeControl(p.tc, p.inc);
          return (
            <button
              key={p.label}
              type="button"
              className={`playing-tc-chip ${hub ? 'playing-tc-chip--hub' : ''} ${active ? 'playing-tc-chip--active' : ''}`}
              aria-pressed={!!active}
              title={speedLabel(speed, t)}
              onClick={() => {
                setCustomOpen(false);
                onChange({ timeControlSeconds: p.tc, incrementSeconds: p.inc });
              }}
            >
              <span className="playing-tc-chip-time">{p.label}</span>
              <span className="playing-tc-chip-speed">{speedLabel(speed, t)}</span>
            </button>
          );
        })}
        <button
          type="button"
          className={`playing-tc-chip ${hub ? 'playing-tc-chip--hub playing-tc-chip--custom' : ''} ${showCustom ? 'playing-tc-chip--active' : ''}`}
          aria-pressed={showCustom}
          onClick={() => setCustomOpen(true)}
        >
          {t('playing.customTime')}
        </button>
      </div>
      {showCustom && (
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-slate-700">
            {t('playing.minutes')}
            <input
              type="number"
              min={1}
              max={180}
              className="input mt-1"
              value={Math.round((timeControlSeconds || 600) / 60)}
              onChange={(e) => {
                setCustomOpen(true);
                const mins = Number(e.target.value);
                const seconds = Number.isFinite(mins) && mins > 0
                  ? Math.min(180, Math.max(1, Math.floor(mins))) * 60
                  : 600;
                onChange({
                  timeControlSeconds: seconds,
                  incrementSeconds,
                });
              }}
            />
          </label>
          <label className="text-sm text-slate-700">
            {t('playing.increment')}
            <input
              type="number"
              min={0}
              max={60}
              className="input mt-1"
              value={incrementSeconds || 0}
              onChange={(e) => {
                setCustomOpen(true);
                const inc = Number(e.target.value);
                onChange({
                  timeControlSeconds,
                  incrementSeconds: Number.isFinite(inc) ? Math.max(0, Math.min(60, Math.floor(inc))) : 0,
                });
              }}
            />
          </label>
        </div>
      )}
      <p className="text-xs text-slate-500">
        {t('playing.speedClassLabel', {
          defaultValue: 'Speed class',
        })}
        {': '}
        <span className="font-semibold text-slate-700">{speedLabel(activeSpeed, t)}</span>
      </p>
    </div>
  );
}
